// askSmart-ui/src/pages/TOSP/components/LeaseReview/LeasePdfPane.tsx
import React, { useEffect, useRef, useState } from "react";
import { LeaseAnchor } from "./leaseReviewFields";

// Same legacy build and pinned worker as SMRTDocsFieldPlacementModal and the
// chatbot corpus viewer — the house pdf.js pattern. Keep the worker version in
// lockstep with the installed pdfjs-dist.
const PDFJS_WORKER_SRC =
  "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";

/** Rendered width in CSS pixels. Anchors are fractional, so this is free to change. */
const TARGET_WIDTH = 820;

async function loadPdfjs() {
  const pdfjs = await import(
    /* webpackChunkName: "pdfjs" */ "pdfjs-dist/legacy/build/pdf"
  );
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
  }
  return pdfjs;
}

interface RenderedPage {
  width: number;
  height: number;
  imageUrl: string;
}

interface LeasePdfPaneProps {
  bytes: Uint8Array | null;
  /** Where the field under review sits. Null scrolls nowhere and marks nothing. */
  anchor: LeaseAnchor | null;
  /** Changes whenever the reviewer moves, so the same anchor re-scrolls. */
  step: number;
}

// The lease itself. Every page rendered once and stacked, so the reviewer can
// scroll the whole document freely — and so moving to a field can scroll the
// page it came from into view rather than replacing what's on screen.
export default function LeasePdfPane({ bytes, anchor, step }: LeasePdfPaneProps) {
  const [pages, setPages] = useState<RenderedPage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!bytes) {
      setPages([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const pdfjs = await loadPdfjs();
        // pdf.js takes ownership of the buffer it's handed, so it gets a copy
        // — the caller may still want the bytes for extraction later.
        const doc = await pdfjs.getDocument({ data: bytes.slice() }).promise;
        const rendered: RenderedPage[] = [];

        for (let i = 1; i <= doc.numPages; i += 1) {
          if (cancelled) return;
          const page = await doc.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: TARGET_WIDTH / base.width });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const context = canvas.getContext("2d");
          if (!context) continue;
          await page.render({ canvasContext: context, viewport }).promise;
          rendered.push({
            width: viewport.width,
            height: viewport.height,
            imageUrl: canvas.toDataURL("image/png"),
          });
        }

        if (cancelled) return;
        setPages(rendered);
      } catch (err) {
        if (cancelled) return;
        console.error("[TOSP] lease render failed:", err);
        setError("This file couldn't be opened as a PDF.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bytes]);

  // Bring the anchored page into view whenever the reviewer moves. Keyed on
  // `step` as well as the anchor so returning to a field scrolls back to it
  // even though its anchor hasn't changed.
  useEffect(() => {
    if (!anchor || pages.length === 0) return;
    const target = pageRefs.current[anchor.page - 1];
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [anchor, step, pages.length]);

  if (!bytes) {
    return (
      <div className="tosp-lease-pane tosp-lease-pane-empty">
        <p>No lease open.</p>
      </div>
    );
  }

  return (
    <div className="tosp-lease-pane" ref={scrollRef}>
      {loading && <div className="tosp-lease-status">Rendering lease…</div>}
      {error && <div className="tosp-lease-status tosp-lease-status-error">{error}</div>}

      {pages.map((page, index) => (
        <div
          key={index}
          className="tosp-lease-page"
          ref={(el) => {
            pageRefs.current[index] = el;
          }}
          style={{ width: page.width, height: page.height }}
        >
          <img src={page.imageUrl} alt={`Lease page ${index + 1}`} />

          {/* Fractional, so the mark lands in the same place whatever width
              the page was rendered at. */}
          {anchor && anchor.page === index + 1 && (
            <div
              className="tosp-lease-highlight"
              style={{
                left: `${anchor.x * 100}%`,
                top: `${anchor.y * 100}%`,
                width: `${anchor.width * 100}%`,
                height: `${anchor.height * 100}%`,
              }}
            />
          )}

          <span className="tosp-lease-page-number">{index + 1}</span>
        </div>
      ))}
    </div>
  );
}
